'use client';

/**
 * Global search — magnifying glass in the navbar that opens a command-palette
 * modal with text-only typeahead over the user's **cookbooks** and public
 * **recipes**, grouped separately.
 *
 * Keyboard:
 *   - ⌘K / Ctrl+K toggles the modal anywhere on the site
 *   - Esc closes
 *   - ↑/↓ navigates across BOTH groups (cookbooks first, then recipes)
 *   - Enter on a cookbook → /cookbook/{id}; on a recipe → /recipes/{slug}
 *   - Enter on the input → /recipes?q={query} (full hybrid search)
 *
 * Engine: searchGlobalTypeaheadAction (ILIKE on cookbook + recipe titles).
 * Debounce: 200ms. Minimum 2 chars. No Gemini, no cost.
 */

import { useEffect, useMemo, useRef, useState, useTransition } from 'react';
import { createPortal } from 'react-dom';
import { useRouter } from 'next/navigation';
import { useTranslations } from 'next-intl';
import { useFocusTrap } from '@/components/ui/use-focus-trap';
import { Icon } from '@/components/ui/icon';
import { Input } from '@/components/ui/input';
import { ThumbImage } from '@/components/ui/thumb-image';
import { getRecipeCardImageUrl } from '@/lib/utils/image-url';
import {
  searchGlobalTypeaheadAction,
  type GlobalTypeaheadResults,
} from '@/lib/actions/global-search';
import styles from './global-search.module.css';

// A single navigable option. The two groups are flattened — cookbooks first,
// then recipes — so ↑/↓ and aria-activedescendant work across both.
type FlatItem =
  | { kind: 'cookbook'; id: string; title: string; recipeCount: number }
  | {
      kind: 'recipe';
      id: string;
      slug: string;
      title: string;
      imageUrl: string | null;
      totalMinutes: number | null;
    };

interface GlobalSearchProps {
  /** UI language for typeahead translation preference. */
  lang?: 'en' | 'de';
}

const EMPTY: GlobalTypeaheadResults = { recipes: [], cookbooks: [] };

export function GlobalSearch({ lang = 'en' }: GlobalSearchProps) {
  const t = useTranslations();
  const router = useRouter();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<GlobalTypeaheadResults>(EMPTY);
  const [focusedIndex, setFocusedIndex] = useState<number>(-1);
  const [isPending, startTransition] = useTransition();
  const inputRef = useRef<HTMLInputElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const flat = useMemo<FlatItem[]>(
    () => [
      ...results.cookbooks.map(
        (c): FlatItem => ({ kind: 'cookbook', id: c.id, title: c.title, recipeCount: c.recipeCount }),
      ),
      ...results.recipes.map(
        (r): FlatItem => ({
          kind: 'recipe',
          id: r.id,
          slug: r.slug,
          title: r.title,
          imageUrl: r.imageUrl,
          totalMinutes: r.totalMinutes,
        }),
      ),
    ],
    [results],
  );

  // ⌘K / Ctrl+K toggles the modal globally
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setOpen((o) => !o);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // Lock body scroll while open
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    setTimeout(() => inputRef.current?.focus(), 0);
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  // Reset transient state on close so reopening starts fresh.
  useEffect(() => {
    if (!open) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setQuery('');
      setResults(EMPTY);
      setFocusedIndex(-1);
    }
  }, [open]);

  useEffect(
    () => () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    },
    [],
  );

  const handleQueryChange = (next: string) => {
    setQuery(next);
    setFocusedIndex(-1);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (next.trim().length < 2) {
      setResults(EMPTY);
      return;
    }
    debounceRef.current = setTimeout(() => {
      startTransition(async () => {
        try {
          const found = await searchGlobalTypeaheadAction(next, lang);
          setResults(found);
        } catch {
          setResults(EMPTY);
        }
      });
    }, 200);
  };

  const openItem = (item: FlatItem) => {
    setOpen(false);
    router.push(item.kind === 'cookbook' ? `/cookbook/${item.id}` : `/recipes/${item.slug}`);
  };

  const goToSearchPage = (q: string) => {
    setOpen(false);
    const params = new URLSearchParams();
    params.set('q', q);
    router.push(`/recipes?${params.toString()}`);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setFocusedIndex((prev) => Math.min(flat.length - 1, prev + 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setFocusedIndex((prev) => Math.max(-1, prev - 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (focusedIndex >= 0 && flat[focusedIndex]) {
        openItem(flat[focusedIndex]);
      } else if (query.trim().length >= 2) {
        goToSearchPage(query.trim());
      }
    }
  };

  return (
    <>
      <button
        type="button"
        className={styles.trigger}
        onClick={() => setOpen(true)}
        aria-label={t('nav.search.label')}
        title={`${t('nav.search.label')} (⌘K)`}
      >
        <Icon name="search" size={18} strokeWidth={1.5} />
        <span className={styles.triggerText}>{t('nav.search.placeholder')}</span>
      </button>

      {open && (
        <GlobalSearchDialog
          query={query}
          results={results}
          flat={flat}
          focusedIndex={focusedIndex}
          isPending={isPending}
          inputRef={inputRef}
          labels={{
            placeholder: t('nav.search.placeholder'),
            searchLabel: t('nav.search.label'),
            seeAllResults: t('nav.search.seeAllResults', { query: query.trim() }),
            noResults: t('nav.search.noResults'),
            closeLabel: t('common.close'),
            recipesGroup: t('nav.search.recipesGroup'),
            cookbooksGroup: t('nav.search.cookbooksGroup'),
            cookbookMeta: (count: number) => t('nav.search.cookbookMeta', { count }),
          }}
          onClose={() => setOpen(false)}
          onQueryChange={handleQueryChange}
          onInputKeyDown={handleKeyDown}
          onPick={openItem}
          onSeeAllResults={() => goToSearchPage(query.trim())}
          onHoverResult={setFocusedIndex}
        />
      )}
    </>
  );
}

interface DialogProps {
  query: string;
  results: GlobalTypeaheadResults;
  flat: FlatItem[];
  focusedIndex: number;
  isPending: boolean;
  inputRef: React.RefObject<HTMLInputElement | null>;
  labels: {
    placeholder: string;
    searchLabel: string;
    seeAllResults: string;
    noResults: string;
    closeLabel: string;
    recipesGroup: string;
    cookbooksGroup: string;
    cookbookMeta: (count: number) => string;
  };
  onClose: () => void;
  onQueryChange: (next: string) => void;
  onInputKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  onPick: (item: FlatItem) => void;
  onSeeAllResults: () => void;
  onHoverResult: (index: number) => void;
}

function GlobalSearchDialog(props: DialogProps) {
  const {
    query,
    results,
    flat,
    focusedIndex,
    isPending,
    inputRef,
    labels,
    onClose,
    onQueryChange,
    onInputKeyDown,
    onPick,
    onSeeAllResults,
    onHoverResult,
  } = props;

  const trapRef = useFocusTrap(onClose);
  const trimmed = query.trim();
  const canSeeAll = trimmed.length >= 2;
  const hasResults = flat.length > 0;

  const renderOption = (item: FlatItem, flatIndex: number) => (
    <li
      key={`${item.kind}-${item.id}`}
      id={`global-search-option-${flatIndex}`}
      role="option"
      aria-selected={flatIndex === focusedIndex}
      className={`${styles.result} ${flatIndex === focusedIndex ? styles.resultFocused : ''}`}
      onMouseDown={(e) => {
        e.preventDefault();
        onPick(item);
      }}
      onMouseEnter={() => onHoverResult(flatIndex)}
    >
      <div className={styles.resultThumb}>
        {item.kind === 'cookbook' ? (
          <span aria-hidden="true" className={styles.resultCookbookIcon}>
            <Icon name="book" size={18} />
          </span>
        ) : item.imageUrl ? (
          <ThumbImage
            thumbSrc={getRecipeCardImageUrl(item.imageUrl)!}
            fallbackSrc={item.imageUrl}
            alt=""
            width={40}
            height={40}
            className={styles.resultImg}
          />
        ) : (
          // eslint-disable-next-line i18next/no-literal-string -- decorative emoji, aria-hidden
          <span aria-hidden="true" className={styles.resultPlaceholder}>🍳</span>
        )}
      </div>
      <div className={styles.resultBody}>
        <span className={styles.resultTitle}>{item.title}</span>
        {item.kind === 'cookbook' ? (
          <span className={styles.resultMeta}>{labels.cookbookMeta(item.recipeCount)}</span>
        ) : item.totalMinutes != null ? (
          // eslint-disable-next-line i18next/no-literal-string -- "min" unit symbol, language-neutral abbreviation
          <span className={styles.resultMeta}>{item.totalMinutes} min</span>
        ) : null}
      </div>
    </li>
  );

  return createPortal(
    <div
      className={styles.backdrop}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        ref={trapRef}
        className={styles.dialog}
        role="dialog"
        aria-modal="true"
        aria-label={labels.searchLabel}
      >
        <div className={styles.inputRow}>
          <Input
            ref={inputRef}
            type="search"
            role="combobox"
            aria-autocomplete="list"
            aria-expanded={hasResults}
            aria-controls="global-search-listbox"
            aria-activedescendant={
              focusedIndex >= 0 ? `global-search-option-${focusedIndex}` : undefined
            }
            value={query}
            onChange={(e) => onQueryChange(e.target.value)}
            onKeyDown={onInputKeyDown}
            placeholder={labels.placeholder}
            autoComplete="off"
            leadingIcon="search"
          />
          {isPending && <span className={styles.spinner} aria-hidden="true" />}
          <button
            type="button"
            className={styles.closeBtn}
            onClick={onClose}
            aria-label={labels.closeLabel}
          >
            <Icon name="x" size={16} strokeWidth={1.75} />
          </button>
        </div>

        {hasResults ? (
          <ul
            id="global-search-listbox"
            role="listbox"
            className={styles.results}
            aria-label={labels.searchLabel}
          >
            {results.cookbooks.length > 0 && (
              <>
                <li role="presentation" className={styles.groupLabel}>
                  {labels.cookbooksGroup}
                </li>
                {results.cookbooks.map((_, i) => renderOption(flat[i], i))}
              </>
            )}
            {results.recipes.length > 0 && (
              <>
                <li role="presentation" className={styles.groupLabel}>
                  {labels.recipesGroup}
                </li>
                {results.recipes.map((_, j) => {
                  const idx = results.cookbooks.length + j;
                  return renderOption(flat[idx], idx);
                })}
              </>
            )}
          </ul>
        ) : (
          trimmed.length >= 2 && !isPending && <p className={styles.empty}>{labels.noResults}</p>
        )}

        {canSeeAll && (
          <button type="button" className={styles.seeAll} onClick={onSeeAllResults}>
            {labels.seeAllResults}
          </button>
        )}
      </div>
    </div>,
    document.body,
  );
}
